import * as React from 'react';

import {LeadIcon} from 'shells/icon/svg/lead-icon';
import {Popover} from 'shells/popover/popover';

import type {PeepRelatedLeadStats as PeepRelatedLeadStatsFragment} from 'nutshell-graphql-types';

import {getStatsToRender, getEmptyStateMessage} from './helpers';
import {PeepRelatedLeadCards} from './peep-related-lead-cards';
import {PeepRelatedLeadStat} from './peep-related-lead-stat';

import styles from './peep-related-lead-stats.module.css';

type Props = {
    peep: PeepRelatedLeadStatsFragment | null | undefined;
    isLoading: boolean;
    errorMessage: string | null | undefined;
    showChildLeadInfo?: boolean;
};

export function PeepRelatedLeadStats(props: Props) {
    const {peep, showChildLeadInfo, isLoading, errorMessage} = props;
    const [isShowingRelatedLeadsPopover, setIsShowingRelatedLeadsPopover] = React.useState(false);
    const popoverAnchorRef = React.useRef(null);

    if (!peep) {
        return null;
    }

    const statsToRender = getStatsToRender(peep, showChildLeadInfo);

    const handleShowRelatedLeads = () => {
        if (peep && peep.leads.stats.all.length) {
            setIsShowingRelatedLeadsPopover(true);
        }
    };

    const handleHideRelatedLeads = () => {
        setIsShowingRelatedLeadsPopover(false);
    };

    return (
        <>
            {isLoading || errorMessage ? (
                <div className={styles['peep-related-lead-stats']}>
                    {/* Purposefully not rendering a loading state here,
                        it's a quick request, and its more distracting
                        to just show a spinner */}
                    {props.isLoading ? undefined : <div>{props.errorMessage}</div>}
                </div>
            ) : (
                <div
                    ref={popoverAnchorRef}
                    onClick={handleShowRelatedLeads}
                    className={
                        statsToRender.length
                            ? styles['peep-related-lead-stats--clickable']
                            : styles['peep-related-lead-stats']
                    }
                >
                    {statsToRender.map((stat) => {
                        return <PeepRelatedLeadStat key={stat.status} stat={stat} />;
                    })}
                    {statsToRender.length === 0 ? (
                        <div className={styles['empty-state']}>
                            <LeadIcon wrapWithDiv={false} size={24} />
                            {getEmptyStateMessage(peep, showChildLeadInfo)}
                        </div>
                    ) : undefined}
                </div>
            )}
            {isShowingRelatedLeadsPopover ? (
                <Popover
                    preventBodyScroll={true}
                    onBlur={handleHideRelatedLeads}
                    location='bottom-left'
                    customStyles={{marginLeft: -24}}
                    anchor={popoverAnchorRef.current}
                >
                    <PeepRelatedLeadCards
                        peepId={peep.id}
                        onNavigate={handleHideRelatedLeads}
                        showChildLeadInfo={showChildLeadInfo}
                        title={showChildLeadInfo ? 'Child leads' : 'Related leads'}
                    />
                </Popover>
            ) : undefined}
        </>
    );
}
